import { motion } from "framer-motion";
import { BRAND, IMAGES } from "../lib/constants";

const words = ["Individuelle", "Webseiten", "&", "smarte", "Beratung"];
const words2 = ["für", "Ihren", "Erfolg"];

export const Hero = () => {
    const scrollTo = (id) => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="home"
            className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
            data-testid="hero-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                {/* Left: big headline */}
                <div className="lg:col-span-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        className="mb-8 w-40 h-40 md:w-56 md:h-56 mx-auto"
                        data-testid="hero-logo"
                    >
                        <img
                            src={BRAND.logo}
                            alt="LS Design & Consulting Logo"
                            className="w-full h-full object-contain mix-blend-multiply"
                        />
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="text-xs uppercase tracking-[0.3em] text-ls-inkSoft font-sans mb-8"
                    >
                        <span className="inline-block w-10 h-px bg-ls-ink align-middle mr-3" />
                        LS Design & Consulting · Est. 2026
                    </motion.p>

                    <h1
                        className="font-serif text-[44px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[96px] text-ls-ink tracking-[-0.02em] text-balance"
                        data-testid="hero-headline"
                    >
                        <span className="block">
                            {words.map((w, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.9,
                                        delay: 0.15 + i * 0.08,
                                        ease: [0.2, 0.7, 0.2, 1],
                                    }}
                                    className="inline-block mr-3"
                                >
                                    {w === "&" ? (
                                        <em className="italic font-light text-ls-accent">
                                            {w}
                                        </em>
                                    ) : (
                                        w
                                    )}
                                </motion.span>
                            ))}
                        </span>
                        <span className="block mt-1">
                            {words2.map((w, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.9,
                                        delay: 0.7 + i * 0.08,
                                        ease: [0.2, 0.7, 0.2, 1],
                                    }}
                                    className={`inline-block mr-3 ${
                                        w === "Erfolg"
                                            ? "italic font-light"
                                            : ""
                                    }`}
                                >
                                    {w}
                                </motion.span>
                            ))}
                            <motion.span
                                initial={{ width: 0 }}
                                animate={{ width: "6rem" }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="inline-block align-middle h-[3px] bg-ls-ink ml-2 mb-2"
                            />
                        </span>
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
                    >
                        <button
                            onClick={() => scrollTo("#contact")}
                            className="group inline-flex items-center gap-3 bg-ls-ink text-ls-bg px-8 py-4 rounded-full text-xs uppercase tracking-[0.24em] font-sans hover:bg-ls-accent transition-all shadow-lg hover:shadow-xl"
                            data-testid="hero-cta-primary"
                        >
                            Jetzt Beratung anfragen
                            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                                →
                            </span>
                        </button>
                        <button
                            onClick={() => scrollTo("#services")}
                            className="inline-flex items-center gap-2 text-sm font-sans text-ls-ink hover:text-ls-accent transition-colors ls-underline"
                            data-testid="hero-cta-secondary"
                        >
                            Unsere Leistungen entdecken
                        </button>
                    </motion.div>
                </div>

                {/* Right: image with float */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="lg:col-span-4 relative"
                >
                    <div className="relative aspect-[4/5] w-full overflow-hidden rounded-sm border border-ls-ink/10 bg-ls-surface2">
                        <motion.img
                            src={IMAGES.hero}
                            alt="Minimalist geometric composition"
                            className="w-full h-full object-cover"
                            initial={{ scale: 1.1 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 2, ease: "easeOut" }}
                        />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                        className="absolute -bottom-6 -left-6 md:-left-10 bg-ls-ink text-ls-bg px-6 py-5 shadow-xl max-w-[220px]"
                    >
                        <p className="font-serif italic text-xl leading-tight">
                            „Design ist klare Kommunikation."
                        </p>
                    </motion.div>
                </motion.div>
            </div>

            {/* Scroll hint */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="hidden md:flex items-center gap-3 absolute bottom-10 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-ls-inkSoft font-sans"
            >
                <span className="w-10 h-px bg-ls-inkSoft animate-pulse" />
                Scroll
            </motion.div>
        </section>
    );
};

export default Hero;
